import { useState, useMemo, useEffect } from 'react';
import CommonModal from '../../common/Modal';
import {
    useFirestore,
    useFirestoreNoCompagny,
    useFirestoreNoCompagnyGeneralOrderBy,
    useFirestoreOrderBy,
} from '../../../firebase/useFirestore';
import { client } from '../../../hooks/Client';
import {
    subscribeToActivity,
    subscribeToOutputs,
    subscribeToEventFields,
} from '../../synchronisations/synchronisationActions';
import linkedSourceIds from '../../../helpers/synchronisations/linkedSourceIds';

// `outputs` and `date` event fields are not syncable as activity fields —
// outputs sync via the Outputs collection, date fields carry no data.
const NON_SYNCABLE_EVENT_FIELD_TYPES = ['outputs', 'date'];

// Subscribe-side: a project picks whole activities (with all outputs) or
// specific outputs + event fields (attached to one of the project's own
// activities) from a parent portfolio's catalog. Writes go through
// subscribeToActivity / subscribeToOutputs / subscribeToEventFields — no
// portfolio approval; status starts at 'paired'.
/**
 * @param {object} props
 * @param {boolean} props.modalOpen - Whether the modal is open
 * @param {(open: boolean) => void} props.setModalOpen - Setter to close the modal
 * @param {string} props.preselectedPortfolioId - CompagnyID of the portfolio whose activities are shown
 * @param {(added: Array<{id: string, docid: string}>) => void} [props.onSubscribed] - Called with the locally created activity after a whole-activity subscribe
 */
const SubscribePortfolioActivitiesModal = ({ modalOpen, setModalOpen, preselectedPortfolioId, onSubscribed }) => {
    const [selectedPortfolioId, setSelectedPortfolioId] = useState('');
    const [expandedActivityId, setExpandedActivityId] = useState('');
    const [activityMode, setActivityMode] = useState({});
    const [pickedOutputs, setPickedOutputs] = useState({});
    const [pickedEventFields, setPickedEventFields] = useState({});
    const [targetActivityByActivity, setTargetActivityByActivity] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // The chooser modal in Activities.jsx pre-picks the portfolio per option, so
    // we sync that down whenever it changes.
    useEffect(() => {
        if (preselectedPortfolioId) setSelectedPortfolioId(preselectedPortfolioId);
    }, [preselectedPortfolioId]);

    const allPortfolioMeta = useFirestoreNoCompagny('CompagnyMeta', 'CompagnyID', selectedPortfolioId || '__none__');
    const portfolioName = allPortfolioMeta?.[0]?.CommunityName || selectedPortfolioId;

    const portfolioActivities = useFirestoreNoCompagnyGeneralOrderBy('Activities', 'CompagnyID', selectedPortfolioId || '__none__', 'Position', 'asc');
    const portfolioOutputs = useFirestoreNoCompagnyGeneralOrderBy('Outputs', 'CompagnyID', selectedPortfolioId || '__none__', 'Position', 'asc');
    // EventForms / EventFields are unordered — not every doc carries Position,
    // and an orderBy query would silently drop the ones that don't.
    const portfolioEventForms = useFirestoreNoCompagny('EventForms', 'CompagnyID', selectedPortfolioId || '__none__');
    const portfolioEventFields = useFirestoreNoCompagny('EventFields', 'CompagnyID', selectedPortfolioId || '__none__');
    const projectActivities = useFirestoreOrderBy('Activities', 'Position', 'asc');
    const projectOutputs = useFirestoreOrderBy('Outputs', 'Position', 'asc');
    const projectEventFields = useFirestore('EventFields');
    const projectSyncs = useFirestoreNoCompagnyGeneralOrderBy(
        'Synchronisations',
        'Project',
        client,
        'Timestamp',
        'desc'
    );

    // Soft-deleted docs (Deleted: true, ADR-008) stay in Firestore but must
    // not be offered for linking.
    const portfolioActivitiesList = useMemo(
        () => (Array.isArray(portfolioActivities) ? portfolioActivities : []).filter((a) => !a?.Deleted),
        [portfolioActivities]
    );

    // An activity this project already took over can't be taken over again —
    // that gave a second copy (#291). Locked until the links are loaded.
    // AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
    const syncsLoaded = Array.isArray(projectSyncs) && Array.isArray(projectActivities);
    const linkedActivityIds = useMemo(
        () => linkedSourceIds(projectSyncs, selectedPortfolioId, ['Activity'], projectActivities),
        [projectSyncs, selectedPortfolioId, projectActivities]
    );

    const outputsByActivity = useMemo(() => {
        const map = {};
        (Array.isArray(portfolioOutputs) ? portfolioOutputs : []).filter((o) => !o?.Deleted).forEach((o) => {
            if (!map[o.ActivityID]) map[o.ActivityID] = [];
            map[o.ActivityID].push(o);
        });
        return map;
    }, [portfolioOutputs]);

    // EventFields link to an activity via EventForms (EventField.EventId →
    // EventForm.ID → EventForm.ActivityID).
    const eventFieldsByActivity = useMemo(() => {
        const activityByFormId = {};
        (Array.isArray(portfolioEventForms) ? portfolioEventForms : []).forEach((form) => {
            if (form.ID) activityByFormId[form.ID] = form.ActivityID;
        });

        const map = {};
        (Array.isArray(portfolioEventFields) ? portfolioEventFields : []).forEach((field) => {
            if (field?.Deleted) return;
            if (NON_SYNCABLE_EVENT_FIELD_TYPES.includes(field.Type)) return;
            const activityId = activityByFormId[field.EventId];
            if (!activityId) return;
            if (!map[activityId]) map[activityId] = [];
            map[activityId].push(field);
        });
        return map;
    }, [portfolioEventFields, portfolioEventForms]);

    const resetState = () => {
        setExpandedActivityId('');
        setActivityMode({});
        setPickedOutputs({});
        setPickedEventFields({});
        setTargetActivityByActivity({});
        setBusy(false);
        setError('');
    };

    const closeModal = () => {
        resetState();
        setSelectedPortfolioId('');
        setModalOpen(false);
    };

    const handleSubscribeWholeActivity = async (activity) => {
        if (busy) return;
        setBusy(true);
        setError('');

        const outputs = outputsByActivity[activity.ID] || [];
        let added;
        try {
            added = await subscribeToActivity({
                portfolioActivity: activity,
                portfolioOutputs: outputs,
                parentId: selectedPortfolioId,
                activitiesLength: (projectActivities || []).length,
                outputsLength: (projectOutputs || []).length,
                syncsLength: (projectSyncs || []).length,
            });
        } catch (err) {
            setError(err.message);
            setBusy(false);
            return;
        }

        if (typeof onSubscribed === 'function') onSubscribed([added]);
        closeModal();
    };

    // Subscribe the picked outputs and/or event fields onto the chosen project
    // activity. Both attach to the same target activity.
    const handleSubscribeSelected = async (activity) => {
        if (busy) return;

        const targetActivityId = targetActivityByActivity[activity.ID];
        const pickedOut = pickedOutputs[activity.ID];
        const pickedFields = pickedEventFields[activity.ID];
        const hasOutputs = !!pickedOut && pickedOut.size > 0;
        const hasFields = !!pickedFields && pickedFields.size > 0;

        if ((!hasOutputs && !hasFields) || !targetActivityId) return;

        setBusy(true);

        const outputs = (outputsByActivity[activity.ID] || []).filter((o) => pickedOut?.has(o.ID));
        const eventFields = (eventFieldsByActivity[activity.ID] || []).filter((f) => pickedFields?.has(f.ID));

        if (hasOutputs) {
            await subscribeToOutputs({
                portfolioOutputs: outputs,
                targetActivityId,
                parentId: selectedPortfolioId,
                outputsLength: (projectOutputs || []).length,
                syncsLength: (projectSyncs || []).length,
            });
        }

        if (hasFields) {
            await subscribeToEventFields({
                portfolioEventFields: eventFields,
                targetActivityId,
                parentId: selectedPortfolioId,
                fieldsLength: (projectEventFields || []).length,
                // Offset past the output sync rows written just above.
                syncsLength: (projectSyncs || []).length + (hasOutputs ? outputs.length : 0),
            });
        }

        closeModal();
    };

    const togglePicked = (setFn) => (activityId, itemId) => {
        setFn((prev) => {
            const current = new Set(prev[activityId] || []);
            if (current.has(itemId)) current.delete(itemId);
            else current.add(itemId);
            return { ...prev, [activityId]: current };
        });
    };
    const togglePickedOutput = togglePicked(setPickedOutputs);
    const togglePickedEventField = togglePicked(setPickedEventFields);

    const renderActivityRow = (activity) => {
        const outputs = outputsByActivity[activity.ID] || [];
        const eventFields = eventFieldsByActivity[activity.ID] || [];
        const isExpanded = expandedActivityId === activity.ID;
        const isLinked = linkedActivityIds.has(activity.ID);
        const mode = activityMode[activity.ID] || 'whole';
        const pickedOut = pickedOutputs[activity.ID] || new Set();
        const pickedFields = pickedEventFields[activity.ID] || new Set();
        const targetActivityId = targetActivityByActivity[activity.ID] || '';
        const canSubscribeSelection =
            (pickedOut.size > 0 || pickedFields.size > 0) && !!targetActivityId;

        return (
            <div key={activity.ID} className="subscribe-modal__item">
                <div className="subscribe-modal__item-header">
                    <p className="subscribe-modal__item-title">
                        {activity.Activity || '(zonder titel)'}{' '}
                        <span className="subscribe-modal__item-meta">
                            ({outputs.length} outputs, {eventFields.length} velden)
                        </span>
                        {isLinked && <span className="subscribe-modal__linked-badge">Al gekoppeld</span>}
                    </p>
                    {!isExpanded && (
                        <button onClick={() => setExpandedActivityId(activity.ID)}>Opties</button>
                    )}
                    {isExpanded && (
                        <button onClick={() => setExpandedActivityId('')}>Inklappen</button>
                    )}
                </div>

                {isExpanded && (
                    <div className="subscribe-modal__drilldown">
                        <div className="subscribe-modal__field">
                            <label>
                                <input
                                    type="radio"
                                    name={`amode-${activity.ID}`}
                                    checked={mode === 'whole'}
                                    onChange={() => setActivityMode((p) => ({ ...p, [activity.ID]: 'whole' }))}
                                />
                                Hele activiteit overnemen (incl. alle outputs)
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name={`amode-${activity.ID}`}
                                    checked={mode === 'selection'}
                                    onChange={() => setActivityMode((p) => ({ ...p, [activity.ID]: 'selection' }))}
                                />
                                Specifieke outputs en velden kiezen
                            </label>
                        </div>

                        {mode === 'whole' && (
                            <button
                                className="subscribe-modal__btn subscribe-modal__btn--primary"
                                disabled={busy || !syncsLoaded || isLinked}
                                onClick={() => handleSubscribeWholeActivity(activity)}
                            >
                                Hele activiteit overnemen
                            </button>
                        )}

                        {mode === 'selection' && (
                            <div className="subscribe-modal__field">
                                {outputs.length === 0 && eventFields.length === 0 ? (
                                    <p>Geen outputs of velden beschikbaar.</p>
                                ) : (
                                    <>
                                        {outputs.length > 0 && (
                                            <>
                                                <label className="subscribe-modal__field-label">Selecteer outputs</label>
                                                <div className="subscribe-modal__checkbox-list">
                                                    {outputs.map((o) => (
                                                        <label key={o.ID} className="subscribe-modal__checkbox-row">
                                                            <input
                                                                type="checkbox"
                                                                checked={pickedOut.has(o.ID)}
                                                                onChange={() => togglePickedOutput(activity.ID, o.ID)}
                                                            />
                                                            <span>{o.Title || '(zonder titel)'}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {eventFields.length > 0 && (
                                            <>
                                                <label className="subscribe-modal__field-label">Selecteer velden</label>
                                                <div className="subscribe-modal__checkbox-list">
                                                    {eventFields.map((f) => (
                                                        <label key={f.ID} className="subscribe-modal__checkbox-row">
                                                            <input
                                                                type="checkbox"
                                                                checked={pickedFields.has(f.ID)}
                                                                onChange={() => togglePickedEventField(activity.ID, f.ID)}
                                                            />
                                                            <span>{f.Title || '(zonder titel)'}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        <label className="subscribe-modal__field-label">Doel-activiteit in dit project</label>
                                        {(projectActivities || []).length === 0 ? (
                                            <p className="subscribe-modal__empty">Geen eigen activiteiten gevonden — maak eerst een eigen activiteit aan.</p>
                                        ) : (
                                            <select
                                                className="subscribe-modal__select"
                                                value={targetActivityId}
                                                onChange={(e) => {
                                                    // Waarde EERST uitlezen. `e.target` is het levende
                                                    // DOM-element; leest de updater hem pas als React al
                                                    // draait, dan staat de oude keuze er alweer in.
                                                    // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
                                                    const value = e.target.value
                                                    setTargetActivityByActivity((p) => ({ ...p, [activity.ID]: value }))
                                                }}
                                            >
                                                <option value="">-- Kies een eigen activiteit --</option>
                                                {(projectActivities || []).map((pa) => (
                                                    <option key={pa.ID} value={pa.ID}>
                                                        {pa.Activity || '(zonder titel)'}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        <button
                                            className="subscribe-modal__btn subscribe-modal__btn--primary"
                                            disabled={busy || !canSubscribeSelection}
                                            onClick={() => handleSubscribeSelected(activity)}
                                        >
                                            Geselecteerde items overnemen
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <CommonModal
            modalOpen={modalOpen}
            setModalOpen={(value) => { if (!value) closeModal(); else setModalOpen(true); }}
            pageHeader={selectedPortfolioId ? `Activiteiten uit ${portfolioName} koppelen` : 'Activiteiten uit portfolio koppelen'}
            hideConfirm={true}
            content={
                <div className="subscribe-modal">
                    {error && <p className="subscribe-modal__error">{error}</p>}
                    {!selectedPortfolioId ? (
                        <p className="subscribe-modal__empty">Geen portfolio geselecteerd.</p>
                    ) : portfolioActivitiesList.length === 0 ? (
                        <p className="subscribe-modal__empty">Geen koppelbare activiteiten gevonden in deze portfolio.</p>
                    ) : (
                        <div className="subscribe-modal__list">
                            {portfolioActivitiesList.map(renderActivityRow)}
                        </div>
                    )}
                </div>
            }
        />
    );
};

export default SubscribePortfolioActivitiesModal;
